import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { ISettingsProps } from "../../MathFactsGenerator/Models.ts";
import { buildDeck } from "../deck.ts";

const BATCH_TARGET = 40;
const TOP_UP_BELOW = 10;

export interface QuizQueue {
    next(): ProblemData;
}

/**
 * An endless supply of problems for one quiz run.
 *
 * A timed run has no known length, so this draws a batch and refills before it
 * runs dry rather than generating a fixed sheet up front. It goes through
 * `buildDeck`, so the queue inherits the same operation, number ranges and
 * regrouping rules as the flash-card deck and the printed cards.
 *
 * Repeats within a run are fine - a drill is repetition - and a long run
 * exhausts the small single-digit pools anyway.
 *
 * `GenerateDataV2` is non-deterministic: create the queue once into state, never
 * during render. The caller must check `canBuildDeck` first; a queue built from
 * unsolvable settings has nothing to serve.
 */
export function createQuizQueue(settings: ISettingsProps): QuizQueue {
    let buffer: ProblemData[] = [];

    function refill() {
        // buildDeck returns up to DECK_SIZE (20); a couple of draws make a batch.
        let guard = 0;
        while (buffer.length < BATCH_TARGET && guard < 4) {
            guard++;
            const drawn = buildDeck(settings);
            if (drawn.length === 0) {
                return;
            }
            buffer = buffer.concat(drawn);
        }
    }

    refill();

    return {
        next(): ProblemData {
            if (buffer.length <= TOP_UP_BELOW) {
                refill();
            }
            const problem = buffer.shift();
            if (!problem) {
                // Only reachable if a run was started from settings that cannot
                // generate anything. Fail here, where the cause is named, rather
                // than let an undefined problem reach the renderer and blow up
                // somewhere that gives no clue why.
                throw new Error(
                    "Quiz queue is empty: these settings generate no problems. " +
                        "Check canBuildDeck before starting a run."
                );
            }
            return problem;
        },
    };
}
